import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Mention from '@tiptap/extension-mention';
import { NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer, ReactRenderer } from '@tiptap/react';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';

import { Member } from '@/features/backlog/types';
import MemberAvatar from '@/components/member-avatar';
import { cn } from '@/components/ui/cn';

interface MItem {
  id: string;
  label: string;
  member: Member;
}

interface ListHandle {
  onKeyDown: (p: SuggestionKeyDownProps) => boolean;
}

const MentionList = forwardRef<ListHandle, SuggestionProps<MItem>>((props, ref) => {
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => setSel(0), [props.items]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${sel}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const pick = (i: number) => {
    const item = props.items[i];
    if (item) props.command({ id: item.id, label: item.label } as unknown as MItem);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') { setSel((s) => (s + props.items.length - 1) % props.items.length); return true; }
      if (event.key === 'ArrowDown') { setSel((s) => (s + 1) % props.items.length); return true; }
      if (event.key === 'Enter') { pick(sel); return true; }
      return false;
    },
  }));

  if (props.items.length === 0) return <div className="rounded-xl border border-line bg-surface-raised p-3 text-[12px] text-fg-faint shadow-xl">No members</div>;
  return (
    <div ref={listRef} className="max-h-[280px] w-[240px] overflow-auto rounded-xl border border-line bg-surface-raised p-1.5 shadow-xl">
      {props.items.map((it, i) => (
        <button
          key={it.id}
          type="button"
          data-idx={i}
          onMouseEnter={() => setSel(i)}
          onMouseDown={(e) => { e.preventDefault(); pick(i); }}
          className={cn('flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors', i === sel ? 'bg-layer-strong' : 'hover:bg-layer')}
        >
          <MemberAvatar member={it.member} size={22} />
          <span className="min-w-0 flex-1 truncate text-[13px] text-fg-soft">{it.label}</span>
        </button>
      ))}
    </div>
  );
});
MentionList.displayName = 'MentionList';

function makeRenderer() {
  let component: ReactRenderer<ListHandle, SuggestionProps<MItem>> | null = null;
  let el: HTMLDivElement | null = null;
  const place = (rect: (() => DOMRect | null) | null | undefined) => {
    if (!el || !rect) return;
    const r = rect();
    if (!r) return;
    el.style.top = `${r.bottom + 6}px`;
    el.style.left = `${Math.min(r.left, window.innerWidth - 256)}px`;
  };
  return {
    onStart: (props: SuggestionProps<MItem>) => {
      component = new ReactRenderer(MentionList, { props, editor: props.editor });
      el = document.createElement('div');
      el.style.position = 'fixed';
      el.style.zIndex = '120';
      document.body.appendChild(el);
      el.appendChild(component.element);
      place(props.clientRect);
    },
    onUpdate: (props: SuggestionProps<MItem>) => { component?.updateProps(props); place(props.clientRect); },
    onKeyDown: (props: SuggestionKeyDownProps) => {
      if (props.event.key === 'Escape') { el?.remove(); el = null; component?.destroy(); component = null; return true; }
      return component?.ref?.onKeyDown(props) ?? false;
    },
    onExit: () => { el?.remove(); el = null; component?.destroy(); component = null; },
  };
}

/**
 * The wiki "@mention" node. Typing "@" opens a member autocomplete; the inserted chip renders as
 * "@Name" but its PLAINTEXT projection (editor.getText()) is the token "@[Name](<member-id>)" — the
 * exact format the backend scans so the mention lands in the Mentions tab. Members are read live via
 * getMembers() so the extension never needs re-creating when the roster changes.
 */
export function createWikiMention(getMembers: () => Member[]) {
  // The chip: an em-scaled mnemonic avatar (initials on the member's colour) + the name, so it
  // harmonises with whatever font size it sits in (body vs a heading).
  const MentionChip = ({ node, editor, getPos }: NodeViewProps) => {
    const id = node.attrs.id as string;
    const label = (node.attrs.label as string) || id;
    const member = getMembers().find((m) => m.id === id) ?? null;
    // The selection RING is applied by the SelectionRing decoration in wiki-editor (adds
    // `.wiki-mention-selected` straight onto this DOM) — the node view must NOT re-render on selection or
    // React would overwrite that class. Inline atoms don't node-select on their own click, so select on
    // mousedown; the decoration then draws the ring and Delete removes it.
    const onMouseDown = (e: React.MouseEvent) => {
      if (!editor.isEditable || typeof getPos !== 'function') return;
      const pos = getPos();
      if (pos == null) return;
      e.preventDefault();
      e.stopPropagation();
      // Use TipTap's own command so the NodeSelection is built with the editor's (single) prosemirror
      // copy — a hand-built NodeSelection can be a foreign class if prosemirror-state is duplicated.
      editor.chain().focus().setNodeSelection(pos).run();
    };
    return (
      <NodeViewWrapper as="span" className="wiki-mention" contentEditable={false} data-id={id} onMouseDown={onMouseDown}>
        <MemberAvatar member={member} size={18} />
        <span>{label}</span>
      </NodeViewWrapper>
    );
  };
  return Mention.extend({
    name: 'mention',
    atom: true,
    selectable: true,
    addNodeView() {
      return ReactNodeViewRenderer(MentionChip);
    },
    // Recognise a copied chip when pasting into ANOTHER page (a different editor instance, where
    // ProseMirror's in-memory slice doesn't apply — so it round-trips through the clipboard HTML that
    // renderHTML below emits). Without a parse rule the chip would paste as plain "@Name" text.
    parseHTML() {
      return [{ tag: 'span[data-type="mention"]' }, { tag: 'span.wiki-mention[data-id]' }];
    },
  }).configure({
    HTMLAttributes: { class: 'wiki-mention' },
    renderText: ({ node }) => `@[${node.attrs.label ?? node.attrs.id}](${node.attrs.id})`,
    // Emit data-type + data-id + data-label so a chip copied to the clipboard parses back into a real
    // mention node (id AND name preserved) on paste into any page — see parseHTML above.
    renderHTML: ({ node }) => ['span', { class: 'wiki-mention', 'data-type': 'mention', 'data-id': node.attrs.id, 'data-label': node.attrs.label ?? node.attrs.id }, `@${node.attrs.label ?? node.attrs.id}`],
    suggestion: {
      char: '@',
      items: ({ query }): MItem[] =>
        getMembers()
          .filter((m) => m.active && `${m.name} ${m.email ?? ''}`.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 6)
          .map((m) => ({ id: m.id, label: m.name, member: m })),
      render: makeRenderer,
    },
  });
}
